import { type MeResponse } from '@diario/common';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { SectionLabel } from '../../components/ui/sectionLabel';
import { sesionQueryOptions } from '../../features/auth/api/useSesion';
import { FormularioReparto } from '../../features/invitations/components/formularioReparto';
import { ListaEnlaces } from '../../features/invitations/components/listaEnlaces';

/**
 * `/enlaces` (documento 04 §10, p. 9 — la ruta que cierra la iteración, T-4.1 «El enlace de
 * organizador»): el formulario de reparto arriba, la lista de enlaces repartidos debajo (decisión
 * ya cerrada, ver el brief de la tarea). Restyle T-3.4 (oleada 3, `gestion-enlaces.html`, la
 * homóloga más fiel de toda la entrega): las dos secciones ganan su propio panel `Card`, igual que
 * el cliente pinta el formulario y la lista como dos tarjetas iguales, y un pie explicativo cierra
 * la pantalla (`pie.nota`) — ningún layout adicional aparte de esos tres bloques.
 *
 * `_autenticado.tsx` ya garantiza que hay sesión antes de llegar aquí; este `beforeLoad` añade el
 * guard propio de esta ruta sobre la MISMA respuesta de `GET /me` que ese padre ya cacheó
 * (`ensureQueryData` no repite la petición si la cache sigue fresca — mismo molde que
 * `_autenticado/admin.tsx`): pasa quien sea `systemRole === 'ADMIN'` o tenga
 * `canInviteOrganizers === true` (documento 04 §7: RN-6 — administración siempre puede, dé igual
 * su subrol; el permiso concedido a dedo es la única puerta para cualquier otra persona). La
 * comparación es `=== true` a propósito, no un `if (sesion.canInviteOrganizers)`: `canInviteOrganizers`
 * es opcional en `MeResponse`, y la intención es que su ausencia signifique "sin permiso" de forma
 * explícita en el código, no por la coerción implícita de `undefined` a falsy.
 *
 * Igual que `/admin`, la puerta se resuelve REDIRIGIENDO (a `/`, mismo destino) en vez de mostrar
 * `estados.sinPermisos` en el sitio: el propio §10.1 lo dice con las mismas palabras que ya usa el
 * documento 02b para `/admin` («no se llega», CA-02b.6) — a diferencia de `/crear-experiencia`
 * (documento 03a), que si no hay sesión SÍ pinta su propia puerta en vez de rebotar, porque ahí
 * hay una decisión de producto explícita de enseñarla; aquí no existe ninguna decisión parecida
 * documentada, así que se sigue el molde exacto que el brief pide imitar, incluido su criterio de
 * fallar cerrado (CA-02b.7): si `GET /me` no se puede comprobar (servidor caído, red…), la puerta
 * se cierra igual que con un rol/permiso incorrecto — nunca se abre "por si acaso". La clave
 * `estados.sinPermisos` queda sin consumidor por esta misma razón: no hace falta explicarlo en
 * ningún sitio si nunca se llega a ver la pantalla.
 */
export const Route = createFileRoute('/_autenticado/enlaces')({
	beforeLoad: async ({ context }) => {
		let sesion: MeResponse;
		try {
			sesion = await context.queryClient.ensureQueryData(sesionQueryOptions);
		} catch {
			throw redirect({ to: '/' });
		}
		if (sesion.systemRole !== 'ADMIN' && sesion.canInviteOrganizers !== true) {
			throw redirect({ to: '/' });
		}
	},
	component: PantallaEnlaces,
});

/**
 * `SectionLabel` (restyle T-3.5, oleada 3, `docs/design-system.md` § Primitivos): el rótulo
 * numerado que antecede al título real de la pantalla, ese en `font-display` (el `<h1>` de
 * `FormularioReparto`, la única cabecera de página de esta ruta — `ListaEnlaces` tiene la suya
 * propia, un `<h2>` con "Tus enlaces", más abajo). `as='p'`, no encabezado: ya hay un `<h1>`
 * justo debajo con el título de verdad, y un segundo encabezado con texto distinto en el mismo
 * tramo de página sería ruido, no jerarquía. Su texto es `shell:nav.enlaces` ("Enlaces") — el namespace
 * `invitations` no declara ningún rótulo corto de sección para esta ruta y esta tarea no puede
 * añadir claves i18n nuevas (ver el informe final de la tarea); `shell.json` ya trae la etiqueta
 * exacta que usaría cualquier futuro enlace de navegación a `/enlaces` (`nav.enlaces`, fuera de
 * alcance de esta iteración), así que reutilizarla aquí no inventa texto nuevo ni lo duplica con
 * ningún otro elemento visible de la pantalla.
 */
function PantallaEnlaces(): React.JSX.Element {
	const { t } = useTranslation(['invitations', 'shell']);
	return (
		<section className='flex flex-col gap-8'>
			<SectionLabel as='p' number='01'>
				{t('shell:nav.enlaces')}
			</SectionLabel>
			<Card>
				<FormularioReparto />
			</Card>
			{/* La lista gana su propio panel blanco (restyle T-3.4 sobre `gestion-enlaces.html`, donde el
			formulario y la lista son dos tarjetas iguales, no una tarjeta y un bloque suelto). */}
			<Card>
				<ListaEnlaces />
			</Card>
			{/* El pie explicativo (`gestion-enlaces.html`): contenido propio sobre RN-04.1/RN-04.2/
			RN-04.5 — nunca la narrativa de encuesta/grupo del cliente, que pertenece al documento 08
			(sin ingerir) y a un flujo que este repositorio no modela. */}
			<Card variant='sunken'>
				<p className='text-gray-ink text-xs leading-normal'>{t('pie.nota')}</p>
			</Card>
		</section>
	);
}
